/**
 * Task pages (ALU-4): the long forms that used to be sheets over a page and
 * are now pages of their own, inside the frame. Each one knows where it
 * returns when there is nothing better (`fallback`, a cold entry from a mail
 * or a reload); a warm entry returns to the page the member came from, with
 * its scroll (hooks/use-list-return.ts, `useTaskReturn`).
 *
 *   /career/mentoring/ask            → Carrera › Mentoría
 *   /career/jobs/new                 → Carrera
 *   /jobs/:slug/edit                 → that job
 *   /profile/:id/edit/:section       → that profile
 *   /profile/:id/linkedin[/updates]  → that profile
 *   /profile/:id/mutual              → that profile (someone else's: ALU-241)
 */

import { destinationForPath, parseMemberPath, type Destination } from './routes'

export const PROFILE_EDIT_SECTIONS = [
  'identity',
  'contact',
  'experience',
  'education',
  'skills',
] as const
export type ProfileEditSectionPath = (typeof PROFILE_EDIT_SECTIONS)[number]

export type TaskPage = {
  /** Where the task returns on a cold entry. */
  fallback: string
  /** The tab it lights up. */
  destination: Destination
  /** It returns to someone else's profile, so "‹ Tu perfil" would be wrong. */
  otherProfile?: true
}

export function taskPageOf(pathname: string): TaskPage | null {
  const parsed = parseMemberPath(pathname)
  if (!parsed) return null
  const base = `/in/${parsed.org}`
  const [a, b, c, d, ...rest] = parsed.rest
  if (rest.length > 0) return null
  if (a === 'career' && b === 'mentoring' && c === 'ask' && !d) {
    return { fallback: `${base}/career?view=mentoring`, destination: 'career' }
  }
  if (a === 'career' && b === 'jobs' && c === 'new' && !d) {
    return { fallback: `${base}/career`, destination: 'career' }
  }
  if (a === 'jobs' && b && c === 'edit' && !d) {
    return { fallback: `${base}/jobs/${b}`, destination: 'career' }
  }
  if (a === 'profile' && b) {
    const profile = { fallback: `${base}/profile/${b}`, destination: 'people' as const }
    if (c === 'edit' && (PROFILE_EDIT_SECTIONS as readonly string[]).includes(d ?? '')) {
      return profile
    }
    if (c === 'linkedin' && (!d || d === 'updates')) return profile
    if (c === 'mutual' && !d) return { ...profile, otherProfile: true }
  }
  return null
}

/** What "‹ …" says for a return target: a profile, a job, or a destination. */
export type ReturnLabel =
  | { kind: 'profile' }
  | { kind: 'job' }
  | { kind: 'destination'; destination: Destination }

export function returnLabelOf(href: string): ReturnLabel {
  const [pathname, search = ''] = href.split('?')
  const rest = parseMemberPath(pathname)?.rest ?? []
  if (rest.length === 2 && rest[0] === 'profile') return { kind: 'profile' }
  if (rest.length === 2 && (rest[0] === 'jobs' || rest[0] === 'opportunities')) {
    return { kind: 'job' }
  }
  return { kind: 'destination', destination: destinationForPath(pathname, search) ?? 'home' }
}

/** URLs of the task pages, so callers never spell them. */
export const taskHref = {
  ask: (org: string) => `/in/${org}/career/mentoring/ask`,
  newJob: (org: string) => `/in/${org}/career/jobs/new`,
  editJob: (org: string, slug: string) => `/in/${org}/jobs/${slug}/edit`,
  editProfile: (org: string, id: string, section: ProfileEditSectionPath) =>
    `/in/${org}/profile/${id}/edit/${section}`,
  linkedin: (org: string, id: string, start?: 'review' | 'paste') =>
    `/in/${org}/profile/${id}/linkedin${start ? `?start=${start}` : ''}`,
  linkedinUpdates: (org: string, id: string) => `/in/${org}/profile/${id}/linkedin/updates`,
  /** The full list of connections someone has in common with you (ALU-241). */
  mutual: (org: string, id: string) => `/in/${org}/profile/${id}/mutual`,
}
